import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { useDispatch, useSelector } from 'react-redux';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import StaticAddColumnDropdownComponent from './StaticAddColumnDropdownComponent';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import {
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  swapProgrammedItemOnChildCalendar,
  setCombineWithInfo,
} from '../../ducks/calendarActions';
import SwapPropDropdownSAQC from './SwapPropDropdownSAQC';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import useSessionState from './hooks/useSessionState';
import { getDebouncedHandlerForTable, flushDebounceForTable } from './hooks/debounceHandler';
import { useFlushDebounceOnMouseMove } from './hooks/useFlushDebounceOnMouseMove';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { normalizeRepToken } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';

const RANGE_FIELDS = new Set(['prescribed value']);

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const WrapperRow = styled('div')`
  display: flex;
  width: 100%;
`;

const StaticDropdownWrapper = styled('div')`
  display: flex;
  margin-left: 16px;
  align-items: self-start;
  margin-top: 17px;
  flex-direction: column;
`;

const RemoveWrapper = styled('div')`
display: flex;
flex-direction: column;
height: 100%;
margin-bottom: 12px;
margin-top: 12px;
gap: 22px;

`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;

const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  margin-bottom: 3px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: 1px solid #EEEEEE;
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const SetRowValue = styled('input')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: #FCFCFC;
  border: 1px solid #EEEEEE;
  text-align: center;
  color: #BDBDBD;
  border-radius: 4px;
`;

const AddSetButtonContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const AddColumnButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const RemoveButtonContainer = styled('div')`
  display: flex;
  min-height: 34px;
`;

const AddSetIcon = styled('div')`
  padding: 4px;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const AddColumnIcon = styled('div')`
  padding: 4px;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RemoveIcon = styled('div')`
  padding: 4px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: ${TransitionGlobal};
  :hover {
    opacity: 0.7;
  }
  cursor: pointer;
`;

const AddSetNumber = styled('input')`
  border-bottom: 1px solid #EEEEEE;
  border-top: none;
  border-left: none;
  border-right: none;
  width: 30px;
  text-align: center;
  height: 16px;
`;

const RowsContainer = styled('div')`
  height: 55px;
  display: flex;
  width: 100%;
  gap: 16px;
`;

const RowValueContainer = styled('div')`
  width: 100%;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SetRowValueContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ isSetsOnly }) => (isSetsOnly
    ? 'width: 100%;'
    : 'min-width: 50px; max-width: 50px;')}
`;

const AddSetText = styled('div')`
  font-weight: 400;
  font-size: 16px;
  font-family: 'Nunito Sans';
  margin-left: 5px;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3px;
`;

const PrimaryValueContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  width: 100%;
  min-width: 180px;
`;

const SetPrimaryValueContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  ${({ isSetsOnly }) => (isSetsOnly
    ? 'width: 100%;'
    : 'min-width: 50px; max-width: 50px;')}
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
  min-width: 250px;
`;

const TooltipDataContainerAthleteInput = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

const ExerciseTableSAQC = ({
  content,
  index,
  coachCompletion,
  completionOnly,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  isRecurring = '',
  assignedPace,
  measure,
  date,
  dayNumber,
}) => {
  const targetRef = useRef(null);
  const targetRefRest = useRef(null);
  const targetRefAthleteInputOnly = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAthleteInputOnlyTooltip, setShowAthleteInputOnlyTooltip] = useState(false);
  const [showTooltipRest, setShowTooltipRest] = useState(false);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const calendarContainerScrollPositionTop = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPositionTop,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);

  const [numberForAdd, setNumberForAdd] = useState(1);

  const [rest, setRest] = useState(null);
  const [intensityUnit, setIntensityUnit] = useState(null);
  const [restPerRepUnit, setRestPerRepUnit] = useState(null);

  const [firstPrimaryRowSwitchLoad, setFirstPrimaryRowSwitchLoad] = useState(true);
  const [firstContentLoad, setFirstContentLoad] = useState(true);
  const [firstLoadDropdownOptions, setFirstLoadDropdownOptions] = useState(true);

  const intensityKey = `intensity (${content.repIntensityUnit || 'bpm'})`;
  const restPerRepKey = `rest per rep (${content.repRestUnit || 'secs'})`;
  /** remove athlete input and prescribed value from initial state of inputs if
   * exercie is completion only
   */
  const primaryRowStateInitialStateIfExerciseIsCompletionOnly = [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'reps' },
    { name: `rest per rep (${content.repRestUnit || 'secs'})` },
    { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
  ];

  const [primaryRows, setPrimaryRows] = useState(content?.exercise?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'athlete input', label: `${capitalizeWords(content?.exercise?.inputTypeDescription)} (${content?.exercise?.unit === 'MS' ? 'm:s' : content?.exercise?.unitAbbr})` },
    { name: 'prescribed value', label: 'Prescribed Value' },
    { name: 'reps' },
    { name: `rest per rep (${content.repRestUnit || 'secs'})` },
    { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
  ]);

  /** remove athlete input and prescribed value from initial state of switch options if
   * exercie is completion only
   */
  const defaultPrimaryRowSwitch = content?.exercise?.inputType === 'C'
    ? {
      sets: true,
      rest: false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    }
    : {
      sets: true,
      rest: false,
      'athlete input': true,
      'prescribed value': false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    };

  /** we need to use session storage for primary row switch values
   * so that if the user adds a field it will not get wiped out
   * between debounces if they haven't entered a value yet,
   * changes to individual table properties will persist the session
   */
  const [primaryRowSwitch, setPrimaryRowSwitchState] = useSessionState(
    'primaryRowSwitchSAQCTable',
    content?.assignedId,
    defaultPrimaryRowSwitch,
  );

  const keySwapDictionary = {
    'prescribed value': 'prescribedValue',
    set: 'setNumber',
    reps: 'reps',
    'intensity (bpm)': 'intensityValue',
    'intensity (RPE)': 'intensityValue',
    'intensity (%)': 'intensityValue',
    'intensity (W)': 'intensityValue',
    'rest per rep (secs)': 'restPerRep',
    'rest per rep (bpm)': 'restPerRep',
  };

  const [rows, setRows] = useState(content?.sets || []);

  // Ref for the table container, used to scope keyboard shortcuts
  const tableContainerRef = useRef(null);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      /** skip first run because cell measurer wrapper
       * will get that and we don't want to run it twice */
      hasMounted.current = true;
      return;
    }
    if (measure) {
      measure();
    }
  }, [rows]);

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const dispatch = useDispatch();

  const formatDate = (dateValue) => `${moment(dateValue).format('YYYY-MM-DD')}`;

  /** debounced function will maintain values at the time of creation
   * on mount unless passed updated values, we can't to make sure we are
   * always using the most updated selected date and current user
   */
  const currentUserRef = useRef(currentUser);
  const selectedDateRef = useRef(effectiveDate);
  const dayNumberRef = useRef(dayNumber);
  useEffect(() => {
    currentUserRef.current = currentUser;
    selectedDateRef.current = effectiveDate;
    dayNumberRef.current = dayNumber;
  }, [currentUser, effectiveDate, dayNumber]);

  useFlushDebounceOnMouseMove(content.assignedId, nonSwappedItemFromParentCalendar);

  const debouncedHandleUpdateSAQCItem = getDebouncedHandlerForTable(content.assignedId, (
    updatedRows,
    dbrest,
    dbPaceUnit,
    dbIntensityUnit,
    dbCoachCompletion,
    dbCompletionOnly,
    dbContent,
    dbIndex,
    dbNonSwappedItemFromParentCalendar,
    dbRestUnit,
    reorder = false,
  ) => {
    const prunedArray = updatedRows.map(({
      prescribedType, percent, ...remainder
    }) => ({
      ...remainder,
    }));
    const updateItem = {
      assignedId: dbContent.assignedId,
      exerciseId: dbContent.exerciseId,
      order: dbIndex,
      sets: prunedArray,
      groupingLetter: dbContent.groupingLetter,
      additionalInfo: dbContent.additionalInfo || null,
      coachCompletion: dbCoachCompletion,
      completionOnly: dbCompletionOnly,
      description: dbContent?.description || null,
      type: typeFormatter(dbContent?.exercise?.typeDescription),
    };
    if (programType === 'calendar') {
      if (!isRecurring) {
        updateItem.date = moment(effectiveDate).format('YYYY-MM-DD');
      } else {
        updateItem.isRecurring = isRecurring;
      }
    }
    if (programType === 'free-form-program') {
      updateItem.dayNumber = dayNumberRef.current;
    }
    if (dbrest) {
      updateItem.restTime = dbrest;
    }
    if (dbPaceUnit) {
      updateItem.pacing = dbPaceUnit;
    }
    if (dbIntensityUnit) {
      updateItem.repIntensityUnit = dbIntensityUnit;
    }
    if (dbRestUnit) {
      updateItem.repRestUnit = dbRestUnit;
    }
    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(
        currentUserRef.current.accountCode,
        updateItem,
        dbIndex,
        dayNumberRef.current,
        reorder,
      ));
    }
    if (programType === 'calendar') {
      const formattedDate = formatDate(selectedDateRef.current);
      if (dbNonSwappedItemFromParentCalendar) {
        dispatch(swapProgrammedItemOnChildCalendar(
          currentUserRef.current.accountCode,
          updateItem,
          dbIndex,
          formattedDate,
          reorder,
        ));
      } else {
        dispatch(updateProgrammedCalendarItem(
          currentUserRef.current.accountCode,
          updateItem,
          dbIndex,
          formattedDate,
          reorder,
        ));
      }
    }
  }, 4000);

  /** This useEffect listens for changes is the exercise chosen from the
     * table dropdown menu that can be used to change the exericse
     * it immediately update the item in redux so we see the change in the table
     * immediately, then we call the debouce update the same way we do
     * for every other input change
     */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const prunedArray = rows.map(({ prescribedType, percent, ...remainder }) => remainder);
      const updatedItem = {
        ...content,
        description: swapDropdownSelectedExercise?.description,
        additionalInfo: swapDropdownSelectedExercise?.additionalInfo,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(effectiveDate),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        exercise: swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise?.name,
        order: index,
        sets: prunedArray,
        coachCompletion,
        completionOnly,
        type: typeFormatter(swapDropdownSelectedExercise.typeDescription),
      };
      if (rest) {
        updatedItem.restTime = rest;
      }
      if (assignedPace) {
        updatedItem.pacing = assignedPace;
      }
      if (intensityUnit) {
        updatedItem.repIntensityUnit = intensityUnit;
      }
      if (restPerRepUnit) {
        updatedItem.repRestUnit = restPerRepUnit;
      }
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, index));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, index));
      }
      debouncedHandleUpdateSAQCItem.cancel();
      debouncedHandleUpdateSAQCItem(
        rows,
        rest,
        assignedPace,
        intensityUnit,
        coachCompletion,
        completionOnly,
        updatedItem,
        index,
        nonSwappedItemFromParentCalendar,
        restPerRepUnit,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [swapDropdownSelectedExercise]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some(
        (item) => item?.assignedId.toString() === content?.assignedId.toString(),
      )
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));

      let groupingLetter = content?.groupingLetter;
      if (isSource) {
        groupingLetter = sourceItem?.groupingLetter;
      } else if (isTarget) {
        groupingLetter = targetItem?.groupingLetter;
      }

      let groupingColorCode = content?.groupingColorCode;
      if (isSource) {
        groupingColorCode = sourceItem?.groupingColorCode;
      } else if (isTarget) {
        groupingColorCode = targetItem?.groupingColorCode;
      }

      const updatedItem = {
        ...content,
        date: formatDate(effectiveDate),
        order: index,
        sets: rows,
        groupingLetter,
        groupingColorCode,
        coachCompletion,
        completionOnly,
      };
      // Combining into a superset has nothing to do with these fields, so a
      // combine must never look like "the coach cleared this value." Local
      // state can be momentarily unavailable when this fires (component
      // remount/render-order race during the drag-drop reorder), so fall
      // back to what's already persisted on `content` rather than letting
      // the field drop out of the request — the update endpoint treats an
      // absent key as an explicit clear.
      const resolvedRest = rest || content.restTime || null;
      const resolvedPace = assignedPace || content.pacing || null;
      const resolvedIntensityUnit = intensityUnit || content.repIntensityUnit || null;
      const resolvedRestUnit = restPerRepUnit || content.repRestUnit || null;
      if (resolvedRest) {
        updatedItem.restTime = resolvedRest;
      }
      if (resolvedPace) {
        updatedItem.pacing = resolvedPace;
      }
      if (resolvedIntensityUnit) {
        updatedItem.repIntensityUnit = resolvedIntensityUnit;
      }
      if (resolvedRestUnit) {
        updatedItem.repRestUnit = resolvedRestUnit;
      }
      const reorder = true;
      debouncedHandleUpdateSAQCItem(
        rows,
        resolvedRest,
        resolvedPace,
        resolvedIntensityUnit,
        coachCompletion,
        completionOnly,
        updatedItem,
        index,
        nonSwappedItemFromParentCalendar,
        resolvedRestUnit,
        reorder,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [combineWithInfo]);

  // Manages focus on input fields when focusTarget changes.
  // If focusTarget's assignedId matches this content's
  // assignedId, finds and focuses the matching input element
  // after a short delay to ensure the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  useEffect(() => {
    if (!firstLoadDropdownOptions) {
      debouncedHandleUpdateSAQCItem(
        rows,
        rest,
        assignedPace,
        intensityUnit,
        coachCompletion,
        completionOnly,
        content,
        index,
        nonSwappedItemFromParentCalendar,
        restPerRepUnit,
      );
      if (nonSwappedItemFromParentCalendar) {
        flushDebounceForTable(content.assignedId);
      }
    } else {
      setFirstLoadDropdownOptions(false);
    }
  }, [coachCompletion, completionOnly, assignedPace]);

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
    debouncedHandleUpdateSAQCItem(
      rows,
      intRest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const setIntensityUnitAndUpdate = (value) => {
    setIntensityUnit(value);
    debouncedHandleUpdateSAQCItem(
      rows,
      rest,
      assignedPace,
      value,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const setRestPerRepUnitAndUpdate = (value) => {
    setRestPerRepUnit(value);
    debouncedHandleUpdateSAQCItem(
      rows,
      rest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      value,
    );
  };

  // Track if all optional columns are enabled to hide the add column button
  const [showAddColumnButton, setShowAddColumnButton] = useState(true);

  // Hide add column button when all optional properties are enabled
  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch['prescribed value']
      && primaryRowSwitch.reps
      && (primaryRowSwitch['rest per rep (secs)'] || primaryRowSwitch['rest per rep (bpm)'])
      && (primaryRowSwitch['intensity (bpm)'] || primaryRowSwitch['intensity (RPE)'] || primaryRowSwitch['intensity (%)'] || primaryRowSwitch['intensity (W)']);

    setShowAddColumnButton(!allOptionalPropertiesEnabled);
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch['prescribed value'],
    primaryRowSwitch.reps,
    primaryRowSwitch['rest per rep (secs)'],
    primaryRowSwitch['rest per rep (bpm)'],
    primaryRowSwitch['intensity (bpm)'],
    primaryRowSwitch['intensity (RPE)'],
    primaryRowSwitch['intensity (%)'],
    primaryRowSwitch['intensity (W)'],
  ]);

  // this is the function that updates the non-set-based values when columns are added
  const setPrimaryRowSwitch = (newState) => {
    setPrimaryRowSwitchState(newState);
    if (!firstPrimaryRowSwitchLoad) {
      setFirstPrimaryRowSwitchLoad(false);
      const initializedRows = [...rows];
      const updatedRows = initializedRows.map((row) => {
        const cleanedRow = { ...row };
        Object.keys(newState).forEach((key) => {
          if (!newState[key]) {
            delete cleanedRow[keySwapDictionary[key]];
          }
        });
        return cleanedRow;
      });
      setRows(updatedRows);
      let newRestValue;
      if (!newState.rest) {
        setRest(null);
        newRestValue = null;
      } else {
        newRestValue = rest;
      }

      let newIntensityUnitValue;
      if (!newState['intensity (bpm)'] && !newState['intensity (RPE)'] && !newState['intensity (%)'] && !newState['intensity (W)']) {
        setIntensityUnit(null);
        newIntensityUnitValue = null;
      } else if (newState['intensity (bpm)']) {
        newIntensityUnitValue = 'bpm';
      } else if (newState['intensity (RPE)']) {
        newIntensityUnitValue = 'RPE';
      } else if (newState['intensity (%)']) {
        newIntensityUnitValue = '%';
      } else if (newState['intensity (W)']) {
        newIntensityUnitValue = 'W';
      }

      let newRestUnitValue;
      if (!newState['rest per rep (secs)'] && !newState['rest per rep (bpm)']) {
        setRestPerRepUnit(null);
        newRestUnitValue = null;
      } else if (newState['rest per rep (secs)']) {
        newRestUnitValue = 'secs';
      } else if (newState['rest per rep (bpm)']) {
        newRestUnitValue = 'bpm';
      }
      debouncedHandleUpdateSAQCItem(
        updatedRows,
        newRestValue,
        assignedPace,
        newIntensityUnitValue,
        coachCompletion,
        completionOnly,
        content,
        index,
        nonSwappedItemFromParentCalendar,
        newRestUnitValue,
      );
    }
    setFirstPrimaryRowSwitchLoad(false);
  };

  // this is the function that sets the appropriate columns and values on load
  useEffect(() => {
    if (content && content.sets) {
      setRows(content.sets);

      const hasIntensityData = content.sets.some(
        (set) => set.intensityValue !== null && set.intensityValue !== undefined && set.intensityValue !== '',
      );

      if (firstContentLoad) {
        // Derive switches entirely from data so stale session-stored flags don't leave
        // empty columns visible after reload following a property removal.
        const freshSwitch = { ...defaultPrimaryRowSwitch };
        freshSwitch['prescribed value'] = content.sets.some(
          (s) => s.prescribedValue !== null && s.prescribedValue !== undefined && s.prescribedValue !== '',
        );
        freshSwitch[restPerRepKey] = content.sets.some(
          (s) => s.restPerRep !== null && s.restPerRep !== undefined && s.restPerRep !== '',
        );
        freshSwitch.reps = content.sets.some(
          (s) => s.reps !== null && s.reps !== undefined && s.reps !== '',
        );
        freshSwitch.rest = !!content.restTime;
        if (content.restTime) setRest(content.restTime);
        if (content.repIntensityUnit || hasIntensityData) {
          freshSwitch[intensityKey] = true;
          if (content.repIntensityUnit) setIntensityUnit(content.repIntensityUnit);
        }
        if (content.repRestUnit) setRestPerRepUnit(content.repRestUnit);
        setPrimaryRowSwitch(freshSwitch);
        setPrimaryRowSwitchState(freshSwitch);
        setFirstContentLoad(false);
      } else {
        // Merge-only so in-session column adds (added but not yet typed into) survive
        // debounced save responses that arrive without that field's data.
        const newPrimaryRowSwitch = { ...defaultPrimaryRowSwitch, ...primaryRowSwitch };
        content.sets.forEach((set) => {
          if (set.prescribedValue !== null && set.prescribedValue !== undefined && set.prescribedValue !== '') {
            newPrimaryRowSwitch['prescribed value'] = true;
          }
          if (set.restPerRep !== null && set.restPerRep !== undefined && set.restPerRep !== '') {
            newPrimaryRowSwitch[restPerRepKey] = true;
          }
          if (set.reps !== null && set.reps !== undefined && set.reps !== '') {
            newPrimaryRowSwitch.reps = true;
          }
        });
        if (content.restTime) {
          newPrimaryRowSwitch.rest = true;
          setRest(content.restTime);
        }
        if (content.repIntensityUnit || hasIntensityData) {
          newPrimaryRowSwitch[intensityKey] = true;
          if (content.repIntensityUnit) setIntensityUnit(content.repIntensityUnit);
        }
        if (content.repRestUnit) setRestPerRepUnit(content.repRestUnit);
        setPrimaryRowSwitchState(newPrimaryRowSwitch);
      }
    }
    /** ADDED FOR SWAPPING EXERCISE NAME - NEED TO UPDATE SETS AND REPS WHEN CONTNENT CHANGES */
    setPrimaryRows(content?.exercise?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
      { name: 'sets' },
      { name: 'rest', label: 'Rest (s)' },
      { name: 'athlete input', label: `${capitalizeWords(content?.exercise?.inputTypeDescription)} (${content?.exercise?.unit === 'MS' ? 'm:s' : content?.exercise?.unitAbbr})` },
      { name: 'prescribed value', label: 'Prescribed Value' },
      { name: 'reps' },
      { name: `rest per rep (${content.repRestUnit || 'secs'})` },
      { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
    ]);
  }, [content]);

  const updateColumnName = useCallback((oldName, newName) => {
    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const addRow = () => {
    if (numberForAdd > 0) {
      const totalRowsAfterAdd = rows.length + numberForAdd;
      if (totalRowsAfterAdd < 11) {
        const lastRow = rows[rows.length - 1];
        const newRows = Array.from({ length: numberForAdd }, (_, i) => ({
          ...lastRow,
          setNumber: rows.length + i + 1,
        }));
        const updatedRows = [...rows, ...newRows];
        setRows(updatedRows);
        debouncedHandleUpdateSAQCItem(
          updatedRows,
          rest,
          assignedPace,
          intensityUnit,
          coachCompletion,
          completionOnly,
          content,
          index,
          nonSwappedItemFromParentCalendar,
          restPerRepUnit,
        );
      } else {
        showErrorToast('The total number of sets cannot exceed 10.');
      }
    }
  };

  const removeRow = (removeIndex) => {
    const updatedRows = rows
      .filter((_, i) => i !== removeIndex) /** Remove the row at removeIndex */
      .map((row, mapIndex) => ({
        ...row,
        setNumber: mapIndex + 1, /** Update setNumber to maintain sequential order */
      }));

    setRows(updatedRows);
    debouncedHandleUpdateSAQCItem(
      updatedRows,
      rest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const parseFieldValue = (field, value) => {
    if (RANGE_FIELDS.has(field) || field.includes('rest per rep')) {
      // Send range/+ tokens as strings; pure numbers as floats
      if (!value && value !== 0) return '';
      if (/^\d+(\.\d+)?$/.test(String(value))) return parseFloat(value);
      return value;
    }
    return value;
  };

  const handleInputChange = (index2, field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'prescribed value' || field.includes('rest per rep') || field === 'reps' || field.includes('intensity')) && parseFloat(value) < 0) {
      return;
    }

    // Prevent negative values for reps field
    let normalizedFieldValue = value;
    if (field === 'reps') {
      // Allow AMAP
      if (value && value.toUpperCase() === 'AMAP') {
        // AMAP is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return; // Prevent negative values in ranges
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        // Check for minimum values (e.g., "8+") but not negative numbers
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return; // Prevent negative minimum values
        }
      } else if (value && !Number.isNaN(Number(value))) {
        // Check for simple numbers (including negative ones)
        if (parseFloat(value) < 0) {
          return; // Prevent negative simple numbers
        }
      }

      // Normalize AMAP case-drift ("Amap" -> "AMAP"). Invalid keyword variants
      // ("AMRAP") are caught at the row-level gate below to block the API call.
      if (value !== '' && value !== null && value !== undefined) {
        const tokenResult = normalizeRepToken(String(value), ['AMAP']);
        if (tokenResult.isValid) {
          normalizedFieldValue = tokenResult.value;
        }
      }
    }

    setRows((prevRows2) => {
      const fieldKey = keySwapDictionary[field];

      // Use normalized value (e.g., "Amap" -> "AMAP") for storage and submission
      const writeValue = field === 'reps' ? normalizedFieldValue : value;

      // Check if this is the first set and we should auto-fill
      const shouldAutoFill = index2 === 0 && writeValue !== null && writeValue !== undefined && writeValue !== '';

      let updatedRows;
      if (shouldAutoFill) {
        // Get the current value in the first set
        const currentFirstSetValue = prevRows2[0][fieldKey];

        // Check if all other sets for this field are empty
        // or have the same value as the current first set
        const otherSetsAreEmptyOrMatchCurrent = prevRows2.slice(1).every((row) => {
          const cellValue = row[fieldKey];
          return cellValue === null || cellValue === undefined || cellValue === '' || cellValue === currentFirstSetValue;
        });

        if (otherSetsAreEmptyOrMatchCurrent) {
          // Auto-fill all sets with the same value
          updatedRows = prevRows2.map((row) => {
            const updatedRow = {
              ...row,
              [fieldKey]: parseFieldValue(field, writeValue),
            };

            // Check if the key is 'prescribedValue' and the value is null or empty
            if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
              delete updatedRow.prescribedValue; // Remove the key
            }

            return updatedRow;
          });
        } else {
          // Only update the current row
          updatedRows = prevRows2.map((row, i) => {
            if (i === index2) {
              const updatedRow = {
                ...row,
                [fieldKey]: parseFieldValue(field, writeValue),
              };

              // Check if the key is 'prescribedValue' and the value is null or empty
              if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
                delete updatedRow.prescribedValue; // Remove the key
              }

              return updatedRow;
            }
            return row;
          });
        }
      } else {
        // Normal update - only change the current row
        updatedRows = prevRows2.map((row, i) => {
          if (i === index2) {
            const updatedRow = {
              ...row,
              [fieldKey]: parseFieldValue(field, writeValue),
            };

            // Check if the key is 'prescribedValue' and the value is null or empty
            if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
              delete updatedRow.prescribedValue; // Remove the key
            }

            return updatedRow;
          }
          return row;
        });
      }

      // Check if any field has empty values before making API call
      const hasEmptyFields = updatedRows.some((row) => {
        const cellValue = row[fieldKey];
        return cellValue === null || cellValue === undefined || cellValue === '';
      });

      // Block API call only when the user is editing reps and their just-typed
      // token is invalid (e.g., "AMRAP"). Silent block — matches the existing
      // negative-number guards above; per-keystroke toasts would spam the UI.
      const editedRepsTokenIsInvalid = field === 'reps'
        && value !== '' && value !== null && value !== undefined
        && !normalizeRepToken(String(value), ['AMAP']).isValid;

      // Only call the API if no fields are empty
      if (!hasEmptyFields && !editedRepsTokenIsInvalid) {
        // Debounce backend update with updated rows
        debouncedHandleUpdateSAQCItem(
          updatedRows,
          rest,
          assignedPace,
          intensityUnit,
          coachCompletion,
          completionOnly,
          content,
          index,
          nonSwappedItemFromParentCalendar,
          restPerRepUnit,
        );
      }

      return updatedRows;
    });
  };

  // Helper function to validate key input for numbers-only fields
  // Pass allowRangeNotation=true for prescribed value, rest per rep, and intensity fields
  const isValidNumberKey = (key, currentValue, allowRangeNotation = false) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow decimal point; for range fields allow one per segment (split by '-')
    if (key === '.') {
      if (allowRangeNotation) {
        const currentSegment = currentValue.split('-').pop();
        if (!currentSegment.includes('.')) return true;
      } else if (!currentValue.includes('.')) {
        return true;
      }
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow dash (range) and plus notation for range fields
    if (allowRangeNotation && (key === '-' || key === '+')) {
      return true;
    }

    return false;
  };

  // Helper function to validate key input for reps fields (allows AMAP and ranges)
  const isValidRepsKey = (key) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow letters for AMAP
    if (/^[a-zA-Z]$/.test(key)) {
      return true;
    }

    // Allow dash for ranges (8-12) and plus for minimums (8+)
    if (key === '-' || key === '+') {
      return true;
    }

    return false;
  };

  const columnInputResolver = (name, rowIndex, row, key) => {
    const fieldValue = row[keySwapDictionary[name]];
    if (name === 'rest') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onKeyDown={(e) => {
              // Prevent commas and validate for numbers-only field
              if (e.key === ',' || (!e.ctrlKey && !isValidNumberKey(e.key, e.target.value))) {
                e.preventDefault();
              }
            }}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === 'athlete input') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            background='#FCFCFC'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }

    const isRangeField = RANGE_FIELDS.has(name) || name.includes('intensity') || name.includes('rest per rep');
    return (
      <RowValueContainer key={key}>
        <RowValue
          type='text' // Change to "text" to allow non-numerical inputs
          aria-label={`${name} for set ${rowIndex + 1}`}
          value={fieldValue || null} // Ensure fallback for undefined/null
          onChange={(e) => {
            handleInputChange(rowIndex, name, e.target.value);
          }}
          onKeyDown={(e) => {
            // Always prevent commas
            if (e.key === ',') {
              e.preventDefault();
              return;
            }

            // Skip validation for disabled fields
            if (name === 'velocity') {
              return;
            }

            // If the user is mutating an AMAP token they're sitting in the middle
            // of, block the keystroke and restore — except Backspace, which
            // deletes the whole token (opt-in via backspaceDeletes).
            if (name === 'reps') {
              const { value, selectionStart, selectionEnd } = e.target;
              const wipeResult = wipeRepKeywordOnMutation(e, value, selectionStart, selectionEnd, ['AMAP'], { backspaceDeletes: true });
              if (wipeResult.wiped) {
                e.preventDefault();
                e.target.value = wipeResult.newValue;
                handleInputChange(rowIndex, name, wipeResult.newValue);
                const inputEl = e.target;
                requestAnimationFrame(() => {
                  inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                });
                return;
              }

              // Single-cell input: once the cell holds AMAP, block any
              // printable-char keystroke that would append/prepend (e.g.,
              // produce "AMAP8"). Wipe-on-edit was handled above.
              const cellTrimmed = (value || '').trim().toUpperCase();
              if (cellTrimmed === 'AMAP'
                  && typeof e.key === 'string'
                  && e.key.length === 1
                  && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
              }
            }

            // Handle AMAP autocomplete for reps field - only when typing 'a' in empty field
            if (name === 'reps' && e.key.toLowerCase() === 'a' && (!e.target.value || e.target.value === '')) {
              e.preventDefault();
              e.target.value = 'AMAP';
              handleInputChange(rowIndex, name, 'AMAP');
              return;
            }

            // Use different validation for reps vs other fields
            const isValid = name === 'reps'
              ? isValidRepsKey(e.key)
              : isValidNumberKey(e.key, e.target.value, isRangeField);

            // Allow Ctrl combinations
            if (!e.ctrlKey && !isValid) {
              e.preventDefault();
            }
          }}
          disabled={name === 'velocity'}
          background={name === 'velocity' ? '#BDBDBD' : '#FCFCFC'}
          data-focus-target={
            // Add focus target to the first editable field that's visible and in the first row
            rowIndex === 0 && (
              (name === 'reps' && primaryRowSwitch.reps)
              || (name === 'prescribed value' && primaryRowSwitch['prescribed value'] && !primaryRowSwitch.reps)
            )
              ? content.assignedId
              : undefined
          }
        />
      </RowValueContainer>
    );
  };

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.exercise.unitAbbr) {
      return `(${unitContent?.exercise?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.exercise?.unitAbbr})`;
    }
    return '';
  };

  const renderPrimaryRowHeader = (primaryRow, isSetsOnly) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    if (primaryRow.name === 'sets') {
      return (
        <SetPrimaryValueContainer key={primaryRow.name} isSetsOnly={isSetsOnly}>
          {capitalizeWords(primaryRow.name)}
        </SetPrimaryValueContainer>
      );
    }

    let refValue = null;
    if (primaryRow.name === 'reps') {
      refValue = targetRef;
    } else if (primaryRow.name === 'athlete input') {
      refValue = targetRefAthleteInputOnly;
    } else if (primaryRow.name === 'rest') {
      refValue = targetRefRest;
    }

    return (
      <PrimaryValueContainer key={primaryRow.name}>
        <PrimaryRowValue>
          <NameAndDropdown ref={refValue}>
            {((primaryRow.name === 'reps') || (primaryRow.name === 'athlete input' && !completionOnly)) && (
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='info2'
                color='rgba(189, 189, 189, 1)'
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => {
                  if (primaryRow.name === 'reps') {
                    setShowTooltip(true);
                  }
                  if (primaryRow.name === 'athlete input') {
                    setShowAthleteInputOnlyTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  if (primaryRow.name === 'reps') {
                    setShowTooltip(false);
                  }
                  if (primaryRow.name === 'athlete input') {
                    setShowAthleteInputOnlyTooltip(false);
                  }
                }}
              />
            )}
            {((primaryRow.name === 'rest') && (
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='info2'
                color='rgba(189, 189, 189, 1)'
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => {
                  if (primaryRow.name === 'rest') {
                    setShowTooltipRest(true);
                  }
                }}
                onMouseLeave={() => {
                  if (primaryRow.name === 'rest') {
                    setShowTooltipRest(false);
                  }
                }}
              />
            ))}
            {primaryRow.name === 'reps' && showTooltip && (
              <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                <TooltipDataContainer>
                  <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
                  <ul style={{ paddingLeft: '20px' }}>
                    <li>
                      <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
                    </li>
                    <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
                    <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
                    <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
                  </ul>
                </TooltipDataContainer>
              </TBportalTooltip>
            )}
            {primaryRow.name === 'athlete input' && showAthleteInputOnlyTooltip && !completionOnly && (
              <TBportalTooltip targetRef={targetRefAthleteInputOnly} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowAthleteInputOnlyTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                <TooltipDataContainerAthleteInput>
                  <Text fontSize='13px' fontWeight={400} color='white'>Athlete input only</Text>
                </TooltipDataContainerAthleteInput>
              </TBportalTooltip>
            )}
            {primaryRow.name === 'rest' && showTooltipRest && (
              <TBportalTooltip targetRef={targetRefRest} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltipRest} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                <TooltipDataContainerAthleteInput>
                  <Text fontSize='13px' fontWeight={400} color='white'>Rest value will be the same for all sets.</Text>
                </TooltipDataContainerAthleteInput>
              </TBportalTooltip>
            )}
            <SwapPropDropdownSAQC
              visible={primaryRow.name !== 'athlete input'}
              setPrimaryRowSwitch={setPrimaryRowSwitch}
              primaryRowSwitch={primaryRowSwitch}
              rowName={primaryRow.name}
              size={12}
              updateColumnName={updateColumnName}
              setIntensityUnit={setIntensityUnitAndUpdate}
              setRestPerRepUnit={setRestPerRepUnitAndUpdate}
              name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
              unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
            />
          </NameAndDropdown>
        </PrimaryRowValue>
      </PrimaryValueContainer>
    );
  };

  const renderValueRowCell = (primaryRow, rowIndex, row) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    if (primaryRow.name === 'sets') {
      const isSetsOnly = primaryRows.filter((r) => primaryRowSwitch[r.name]).length === 1;

      return (
        <SetRowValueContainer key={primaryRow.name} isSetsOnly={isSetsOnly}>
          <SetRowValue
            type='number'
            aria-label={`${primaryRow.name} for set ${rowIndex + 1}`}
            value={rowIndex + 1}
            disabled
            onChange={(e) => handleInputChange(rowIndex, primaryRow.name, e.target.value)}
          />
        </SetRowValueContainer>
      );
    }

    return columnInputResolver(primaryRow.name, rowIndex, row, primaryRow.name);
  };

  const isSetsOnly = primaryRows.filter((r) => primaryRowSwitch[r.name]).length === 1;
  return (
    <Wrapper
      ref={tableContainerRef}
      onBlur={nonSwappedItemFromParentCalendar
        ? () => flushDebounceForTable(content.assignedId)
        : undefined}
    >
      <WrapperRow>
        <Container>
          <PrimaryRow>
            {primaryRows.map((primaryRow) => renderPrimaryRowHeader(primaryRow, isSetsOnly))}
          </PrimaryRow>
          {rows.map((row, rowIndex) => (
            <ValueRow key={row.setNumber || rowIndex}>
              <RowsContainer>
                {primaryRows.map((primaryRow) => renderValueRowCell(primaryRow, rowIndex, row))}
              </RowsContainer>
            </ValueRow>
          ))}
        </Container>
        <StaticDropdownWrapper>
          {showAddColumnButton && (
          <StaticAddColumnDropdownComponent
            primaryRows={primaryRows}
            setPrimaryRows={setPrimaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setIntensityUnit={setIntensityUnit}
            setRestPerRepUnit={setRestPerRepUnit}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            size={12}
            containerRef={tableContainerRef}
            assignedId={content.assignedId}
          >
            <AddColumnButtonContainer className='add-column-button'>
              <AddColumnIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  size={12}
                  icon='plus'
                  color='white'
                />
              </AddColumnIcon>
            </AddColumnButtonContainer>
          </StaticAddColumnDropdownComponent>
          )}
          <RemoveWrapper>
            {rows.map((row, rowIndex) => (
              rows.length !== 1 ? (
                <RemoveButtonContainer
                  key={row.setNumber || rowIndex}
                  onClick={() => removeRow(rowIndex)}
                  tabIndex={-1}
                >
                  <RemoveIcon>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={12}
                      icon='remove'
                      color='#9E9E9E'
                    />
                  </RemoveIcon>
                </RemoveButtonContainer>
              ) : <div key={row.setNumber || rowIndex} style={{ display: 'flex', minWidth: 20, minHeight: 34 }} />
            ))}
          </RemoveWrapper>
        </StaticDropdownWrapper>
      </WrapperRow>
      {rows?.length < 10 && (
      <AddSetButtonContainer>
        <AddSetIcon onClick={addRow}>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='plus'
            color='white'
          />
        </AddSetIcon>
        <AddSetText>
          Add
          {' '}
          <AddSetNumber
            value={numberForAdd}
            onChange={(e) => {
              const inputValue = parseInt(e.target.value, 10);
              // Prevent negative values
              if (inputValue < 0) return;
              setNumberForAdd(inputValue);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRow();
              }
              if (e.key === 'Tab' && !e.shiftKey) {
                // Check if this is the last exercise in the list
                const allExerciseElements = document.querySelectorAll('[class*="AddSetNumber"]');
                const currentIndex = Array.from(allExerciseElements).indexOf(e.target);
                const isLastExercise = currentIndex === allExerciseElements.length - 1;

                if (isLastExercise) {
                  e.preventDefault();
                  // Find and focus the ExercisePicker button
                  const exercisePickerButton = document.querySelector('[class*="AddExerciseButtonContainer"]');
                  if (exercisePickerButton) {
                    exercisePickerButton.focus();
                  }
                }
              }
            }}
            type='number'
          />
          {' '}
          {numberForAdd > 1 ? 'Sets' : 'Set'}
        </AddSetText>
      </AddSetButtonContainer>
      )}
    </Wrapper>
  );
};

ExerciseTableSAQC.defaultProps = {
  isRecurring: '',
  assignedPace: '',
  date: null,
  dayNumber: null,
};

ExerciseTableSAQC.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
};

export default ExerciseTableSAQC;
